import React, { useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { ReactComponent as FingerPointingIcon } from "assets/images/finger-pointing.svg";
import {
  useGetAuthenticatedUserQuery,
  useGetDashboardQuery,
  UserCareerStage,
  useViewDashboardQuery,
} from "generated/graphql";
import { ReactComponent as ArrowLeftIcon } from "assets/images/arrow-left-black.svg";

import SubjectRecommendations from "components/molecules/SubjectRecommendations";
import {
  Container,
  Header,
  MainContent,
  Sidebar,
  TableOfContentsCard,
  TOCTitle,
  TOCList,
  TOCItem,
  ContentArea,
  CareerTitle,
  SectionTitle,
  ContentSection,
  Description,
  VideoPlaceholder,
  SectionHeader,
  SideCardsContainer,
  SideCard,
  SideCardTitle,
  CountrySection,
  CountryHeader,
  CountryName,
  LinksList,
  LinkItem,
  LinkText,
  SideCardWrapper,
  Divider,
} from "./styles";

const CareerDetails = ({
  isDashboardAccess,
  asViewer,
}: {
  isDashboardAccess?: boolean;
  asViewer?: boolean;
}) => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");
  const { testAttemptId } = useParams<{ testAttemptId: string }>();

  const handleBackClick = () => {
    if (isDashboardAccess) {
      history.push(`/dashboard-access?token=${token}`);
    } else if (testAttemptId) {
      history.push(`/dashboard-preview/${testAttemptId}`);
    } else {
      history.push("/student");
    }
  };

  const { data: userData } = useGetAuthenticatedUserQuery();

  const { data: dashboardDataAccess } = useViewDashboardQuery({
    variables: { token: token || "" },
  });

  const { data: dashboardDataRegular } = useGetDashboardQuery({
    variables: {
      testId: asViewer
        ? Number(testAttemptId)
        : Number(userData?.authenticatedItem?.students?.[0]?.testAttempt?.id) ||
          -1,
    },
  });

  const dashboardData = isDashboardAccess
    ? dashboardDataAccess?.viewDashboard
    : dashboardDataRegular?.getDashboard;

  const careerData = dashboardData?.suggestedCareers?.find(
    (careerItem) => careerItem.id === id
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Container>
      <Header onClick={handleBackClick}>
        <ArrowLeftIcon /> BACK
      </Header>

      <MainContent>
        <Sidebar>
          <TableOfContentsCard>
            <TOCTitle>TABLE OF CONTENTS </TOCTitle>
            <TOCList>
              <TOCItem href="#about" active>
                About career
              </TOCItem>

              <TOCItem href="#how-to-become">How to become</TOCItem>
              {dashboardData?.user?.careerStage ===
                UserCareerStage.CareerStarter && (
                <TOCItem href="#subject-recommendations">
                  Subject Recommendations
                </TOCItem>
              )}
              <TOCItem href="#helpful-links">Helpful links</TOCItem>
            </TOCList>
          </TableOfContentsCard>
        </Sidebar>

        <div>
          <ContentArea>
            <CareerTitle>{careerData?.name}</CareerTitle>

            <div id="about">
              <SectionTitle>About career</SectionTitle>
              <ContentSection>
                <Description>
                  {" "}
                  {careerData?.description?.split("\n").map((line, idx, arr) =>
                    idx === arr.length - 1 ? (
                      line
                    ) : (
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    )
                  )}
                </Description>
                {/* <VideoPlaceholder>
                  <span role="img" aria-label="video">
                    📺
                  </span>{" "}
                  YouTube Video Placeholder
                </VideoPlaceholder> */}
              </ContentSection>
            </div>

            <div id="how-to-become">
              <SectionHeader>
                <SectionTitle>To become a/an {careerData?.name}</SectionTitle>
              </SectionHeader>
              <Description>
                {dashboardData?.user?.careerStage ===
                UserCareerStage.CareerStarter
                  ? careerData?.howToBecomeStudent
                      ?.split("\n")
                      .map((line, idx, arr) =>
                        idx === arr.length - 1 ? (
                          line
                        ) : (
                          <React.Fragment key={idx}>
                            {line}
                            <br />
                          </React.Fragment>
                        )
                      )
                  : careerData?.howToBecomeCareerChange
                      ?.split("\n")
                      .map((line, idx, arr) =>
                        idx === arr.length - 1 ? (
                          line
                        ) : (
                          <React.Fragment key={idx}>
                            {line}
                            <br />
                          </React.Fragment>
                        )
                      )}
              </Description>
            </div>

            {dashboardData?.user?.careerStage ===
              UserCareerStage.CareerStarter && (
              <div
                id="subject-recommendations"
                style={{
                  width:
                    window.innerWidth <= 768 ? "calc(100vw - 80px)" : undefined,
                }}
              >
                <SubjectRecommendations
                  subjectRecommendations={
                    dashboardData?.subjectRecommendations || []
                  }
                  allSubjects={dashboardData?.allSubjects || []}
                  selectedCareer={careerData?.name || ""}
                  favouriteCareers={[]}
                />
              </div>
            )}
          </ContentArea>

          <SideCardsContainer id="helpful-links">
            <SideCard isFirst>
              <SideCardTitle>Choosing where to study</SideCardTitle>
              <SideCardWrapper>
                {dashboardData?.whereToStudy
                  ?.filter((item) => item.header !== "Online Training")
                  .map((item, index) => (
                    <React.Fragment key={item.header}>
                      <CountrySection>
                        <CountryHeader>
                          <FingerPointingIcon />

                          <CountryName>{item.header}</CountryName>
                        </CountryHeader>
                        <LinksList>
                          {item.links.map((link) => (
                            <LinkItem href={link.url} key={link.label}>
                              <LinkText>{link.label}</LinkText>
                            </LinkItem>
                          ))}
                        </LinksList>
                      </CountrySection>
                      {index !==
                        (dashboardData?.whereToStudy?.length || 0) - 2 && (
                        <Divider />
                      )}
                    </React.Fragment>
                  ))}
              </SideCardWrapper>
            </SideCard>

            <SideCard>
              <SideCardTitle>Trainings</SideCardTitle>
              {dashboardData?.whereToStudy
                ?.filter((item) => item.header === "Online Training")
                .map((item) => (
                  <CountrySection style={{ width: "200px" }} key={item.header}>
                    <CountryHeader>
                      <FingerPointingIcon />
                      <CountryName>{item.header}</CountryName>
                    </CountryHeader>
                    <LinksList>
                      {item.links.map((link) => (
                        <LinkItem href={link.url} key={link.label}>
                          <LinkText>{link.label}</LinkText>
                        </LinkItem>
                      ))}
                    </LinksList>
                  </CountrySection>
                ))}
            </SideCard>
          </SideCardsContainer>
        </div>
      </MainContent>
    </Container>
  );
};

CareerDetails.defaultProps = {
  isDashboardAccess: false,
  asViewer: false,
};

export default CareerDetails;
